import { Link } from 'react-router-dom';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/ApiConfig';
import TramitacaoChart from './components/TramitacaoChart';
import TipoDfdChart from './components/TipoDfdChart';
import TipoDemandaChart from './components/TipoDemandaChart';
import Layout from './components/Layout';
import { formatString, truncateText } from './utils/StringUtils';
import { formatDateView } from './utils/dateUtils';
import './css/Home.css';

const TRAMITACAO_STYLE = {
  APROVADO: { bg: '#d4edda', color: '#155724', border: '#c3e6cb' },
  'DEVOLVIDO PARA AJUSTES': { bg: '#fff3cd', color: '#856404', border: '#ffeeba' },
  'EM ELABORAÇÃO': { bg: '#d1ecf1', color: '#0c5460', border: '#bee5eb' },
  'ENVIADO PARA APROVAÇÃO': { bg: '#e2d9f3', color: '#4b2e83', border: '#d0b8f0' },
  REPROVADO: { bg: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
};

const DEFAULT_BADGE = { bg: '#e9ecef', color: '#495057', border: '#dee2e6' };

const KPI_CONFIG = [
  { label: 'Total de DFDs', icon: 'bi-clipboard-data', gradient: 'linear-gradient(135deg, #0b3383, #0e48ad)', key: 'total' },
  { label: 'Em Elaboração', icon: 'bi-pencil-square', gradient: 'linear-gradient(135deg, #0d6efd, #54a0ff)', key: 'EM ELABORAÇÃO' },
  { label: 'Enviados para Aprovação', icon: 'bi-send', gradient: 'linear-gradient(135deg, #9966ff, #b388ff)', key: 'ENVIADO PARA APROVAÇÃO' },
  { label: 'Aprovados', icon: 'bi-check-circle', gradient: 'linear-gradient(135deg, #28a745, #51cf66)', key: 'APROVADO' },
  { label: 'DFDs sem Itens', icon: 'bi-box-seam', gradient: 'linear-gradient(135deg, #17a2b8, #5dc4e0)', key: 'semItens' },
  { label: 'Sem Valor Estimado', icon: 'bi-cash-coin', gradient: 'linear-gradient(135deg, #fd7e14, #ffa94d)', key: 'semValorEstimado' },
  { label: 'Devolvidos para Ajustes', icon: 'bi-arrow-return-left', gradient: 'linear-gradient(135deg, #e0a800, #ffc107)', key: 'DEVOLVIDO PARA AJUSTES' },
  { label: 'Reprovados', icon: 'bi-x-circle', gradient: 'linear-gradient(135deg, #dc3545, #ff6b6b)', key: 'REPROVADO' },
];

const Home = () => {
  const [tramitacaoData, setTramitacaoData] = useState({});
  const [tipoData, setTipoData] = useState({});
  const [tipoDemandaData, setTipoDemandaData] = useState({});
  const [dfdData, setDfdData] = useState([]);
  const [semItens, setSemItens] = useState(0);
  const [semValorEstimado, setSemValorEstimado] = useState(0);

  const token = localStorage.getItem('token');
  const cnpj = JSON.parse(localStorage.getItem('appCliente')).cnpj;

  const [anoBase, setAnoBase] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('anoBase'));
    } catch (err) {
      return localStorage.getItem('anoBase');
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dfdResponse = await axios.get(`${API_BASE_URL.DFD}/cnpj`, {
          params: { cnpj: cnpj, anoBase: anoBase },
          headers: { Authorization: `Bearer ${token}` },
        });
        const dfdData = dfdResponse.data;
        setDfdData(dfdData);

        const tramitacaoCounts = dfdData.reduce((acc, dfd) => {
          acc[dfd.tramitacao] = (acc[dfd.tramitacao] || 0) + 1;
          return acc;
        }, {});
        setTramitacaoData(tramitacaoCounts);

        const tipoCounts = dfdData.reduce((acc, dfd) => {
          acc[dfd.tipo] = (acc[dfd.tipo] || 0) + 1;
          return acc;
        }, {});
        setTipoData(tipoCounts);

        const tipoDemandaCounts = dfdData.reduce((acc, dfd) => {
          const tipoDemanda = dfd.tipoDemanda || 'Sem Tipo';
          acc[tipoDemanda] = (acc[tipoDemanda] || 0) + 1;
          return acc;
        }, {});
        setTipoDemandaData(tipoDemandaCounts);

        const semItensResponse = await axios.get(`${API_BASE_URL.DFD}/sem-itens`, {
          params: { cnpj: cnpj, anoBase: anoBase },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSemItens(semItensResponse.data || 0);

        const semValorResponse = await axios.get(`${API_BASE_URL.DFD}/sem-valor-estimado`, {
          params: { cnpj: cnpj, anoBase: anoBase },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSemValorEstimado(semValorResponse.data || 0);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };

    fetchData();

    const handler = e => {
      const newAno = e?.detail?.anoBase ?? (() => {
        try { return JSON.parse(localStorage.getItem('anoBase')); } catch { return localStorage.getItem('anoBase'); }
      })();
      setAnoBase(newAno);
    };
    window.addEventListener('anoBaseChanged', handler);
    return () => window.removeEventListener('anoBaseChanged', handler);
  }, [cnpj, anoBase, token]);

  const totalDfds = dfdData.length;
  const recentDfds = [...dfdData].sort((a, b) => b.num - a.num).slice(0, 6);

  return (
    <Layout>
      <div className="home-scroll">
        {/* Cards de indicadores */}
        <div className="home-kpi-row">
          {KPI_CONFIG.map(k => (
            <div className="home-kpi-item" key={k.key}>
              <div className="home-kpi-card">
                <div className="home-kpi-icon" style={{ background: k.gradient }}>
                  <i className={`bi ${k.icon}`}></i>
                </div>
                <div>
                  <div className="home-kpi-value">
                    {k.key === 'total' ? totalDfds
                      : k.key === 'semItens' ? semItens
                      : k.key === 'semValorEstimado' ? semValorEstimado
                      : tramitacaoData[k.key] || 0}
                  </div>
                  <div className="home-kpi-label">{k.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Gráficos */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-4 d-flex mb-3 mb-lg-0">
            <div className="home-card card shadow-sm w-100">
              <div className="card-body p-3 d-flex flex-column home-chart-body">
                <TipoDfdChart data={tipoData} />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4 d-flex mb-3 mb-lg-0">
            <div className="home-card card shadow-sm w-100">
              <div className="card-body p-3 d-flex flex-column home-chart-body">
                <TipoDemandaChart data={tipoDemandaData} />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4 d-flex mb-3 mb-lg-0">
            <div className="home-card card shadow-sm w-100">
              <div className="card-body p-3 d-flex flex-column home-chart-body">
                <TramitacaoChart data={tramitacaoData} />
              </div>
            </div>
          </div>
        </div>

        {/* DFDs recentes */}
        <div className="home-card card shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span><i className="bi bi-clock-history me-2"></i>DFDs Recentes</span>
            <Link to="/dfds" className="btn btn-sm btn-light">Ver todos</Link>
          </div>
          <div className="card-body p-0">
            {recentDfds.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover align-middle home-table">
                  <thead>
                    <tr>
                      <th>Nº</th>
                      <th>Data de Contratação</th>
                      <th>Objeto</th>
                      <th>Tipo</th>
                      <th>Tramitação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentDfds.map(c => {
                      const badge = TRAMITACAO_STYLE[c.tramitacao] || DEFAULT_BADGE;
                      return (
                        <tr key={`${c.cnpj}-${c.num}`}>
                          <td className="text-nowrap">{formatString(c.num, 4)}</td>
                          <td className="text-nowrap">{formatDateView(c.dataContratacao)}</td>
                          <td>{truncateText(c.objeto || '', 80)}</td>
                          <td className="text-nowrap">{c.tipo}</td>
                          <td>
                            <span
                              className="home-badge"
                              style={{
                                backgroundColor: badge.bg,
                                color: badge.color,
                                border: `1px solid ${badge.border}`,
                              }}
                            >
                              {c.tramitacao}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4 text-center text-muted">
                Nenhum DFD encontrado para o ano base {anoBase}.
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Home;
